import { REAL_REVIEWS } from "@/config/reviews";
import { GOOGLE_REVIEWS_URL, GOOGLE_RATING, PHONE_DISPLAY, PHONE_TEL } from "@/config/site";
import { Star, ExternalLink, Phone, Quote } from "lucide-react";
import { useReveal } from "@/hooks/useReveal";
import TiltCard from "@/components/site/TiltCard";

export default function Testimonials() {
  const headRef = useReveal();
  return (
    <section
      id="reviews"
      className="relative bg-black py-24 sm:py-36 border-t border-white/5 overflow-hidden"
      style={{ perspective: "1400px" }}
    >
      {/* Ambient spotlights (multiple for depth) */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 right-0 w-[540px] h-[440px] opacity-30"
        style={{ background: "radial-gradient(ellipse at center, rgba(250,204,21,0.16) 0%, rgba(0,0,0,0) 65%)" }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute top-1/3 -left-40 w-[520px] h-[440px] opacity-25"
        style={{ background: "radial-gradient(ellipse at center, rgba(59,130,246,0.12) 0%, rgba(0,0,0,0) 65%)" }}
      />
      <div className="pointer-events-none absolute inset-0 bm-grain" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        <div ref={headRef} className="lux-reveal flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-16">
          <div className="max-w-3xl">
            <div className="font-display uppercase tracking-[0.32em] text-yellow-400 text-xs flex items-center gap-2">
              <span className="inline-block h-1.5 w-6 bg-yellow-400 rounded-full" />
              Verified Google Reviews
            </div>
            <h2 className="mt-3 font-display font-black uppercase text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              What real customers <span className="text-yellow-400">actually say.</span>
            </h2>
            <div className="mt-4 flex items-center gap-1" aria-label={`${GOOGLE_RATING} star rating`}>
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} className="h-5 w-5 text-yellow-400" fill="#FACC15" strokeWidth={1.5} />
              ))}
              <span className="ml-3 font-display uppercase tracking-[0.22em] text-[11px] text-zinc-400">
                {GOOGLE_RATING} avg · unedited from Google
              </span>
            </div>
          </div>
          <a
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="testimonials-google-btn"
            className="inline-flex items-center gap-2 lux-glass hover:border-yellow-400/50 text-white font-display font-bold uppercase tracking-wider px-5 py-3 rounded-full transition-colors"
          >
            Read all reviews <ExternalLink className="h-4 w-4" strokeWidth={2.5} />
          </a>
        </div>

        {/* 3D floating review cards */}
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10"
          style={{ perspective: "1400px", perspectiveOrigin: "50% 40%" }}
        >
          {REAL_REVIEWS.map((r, i) => (
            <TiltCard
              key={r.author}
              testId={`review-${r.author.replace(/\s+/g, "-").toLowerCase()}`}
              className="lux-glass rounded-2xl p-7 flex flex-col overflow-hidden"
              floatDelay={(i % 3) * 0.6}
              maxTilt={8}
            >
              <Quote
                className="absolute -top-4 -right-2 h-20 w-20 text-yellow-400/10"
                aria-hidden="true"
                strokeWidth={1}
              />
              <div className="flex items-center gap-1 mb-3" aria-hidden="true">
                {Array.from({ length: r.rating }).map((_, k) => (
                  <Star key={`${r.author}-star-${k}`} className="h-4 w-4 text-yellow-400" fill="#FACC15" strokeWidth={1.5} />
                ))}
              </div>
              <p className="relative text-zinc-200 text-sm leading-relaxed font-light">&ldquo;{r.text}&rdquo;</p>
              <div className="relative mt-6 pt-4 border-t border-white/5 flex items-center justify-between">
                <div>
                  <div className="font-display font-black uppercase text-white text-sm">{r.author}</div>
                  <div className="text-zinc-500 text-xs mt-0.5">{r.when}</div>
                </div>
                <div className="font-display uppercase tracking-[0.2em] text-[10px] text-yellow-400">Google</div>
              </div>
            </TiltCard>
          ))}
        </div>

        <div className="mt-16 relative lux-glass-strong rounded-2xl p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center gap-6 justify-between overflow-hidden">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-20 -left-20 w-64 h-64 rounded-full opacity-40"
            style={{ background: "radial-gradient(circle, rgba(250,204,21,0.35) 0%, rgba(0,0,0,0) 70%)" }}
          />
          <div className="relative font-display font-black uppercase text-white text-2xl sm:text-3xl leading-tight tracking-tight max-w-xl">
            Want to be the next <span className="text-yellow-400">5-star review?</span>
          </div>
          <a
            href={`tel:${PHONE_TEL}`}
            data-testid="testimonials-call-btn"
            className="relative inline-flex items-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-black font-display font-black uppercase tracking-wider px-6 py-3.5 rounded-full lux-glow-yellow transition-colors"
          >
            <Phone className="h-5 w-5" strokeWidth={3} /> Call {PHONE_DISPLAY}
          </a>
        </div>
      </div>
    </section>
  );
}
